import Popover from '@/components/Popover';
import styled from '@/styles';
import { TierBoundary } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { tierColor } from './tierColors';
import { clampScore, displayScore } from './score';
import PopoverSurface from './PopoverSurface';
import { PopoverMedal } from './icons';

const Content = styled(PopoverSurface, {
  padding: '16px 24px',
  width: '348px',
});
const Title = styled('div', {
  fontWeight: 700,
  fontSize: '$lg',
  color: dependabilityV2Colors.depInk,
  marginBottom: '4px',
});
const Subtitle = styled('div', {
  fontSize: '$md',
  color: dependabilityV2Colors.depInk,
  marginBottom: '16px',
  variants: {
    activation: { true: { fontStyle: 'italic', color: '$themeDarker' } },
  },
});
// Medal artwork beside the tier text, per the popover frame (37858-31304)
const TierSummary = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '16px',
});
const TierName = styled('div', {
  fontSize: '$md',
  fontWeight: 700,
  color: dependabilityV2Colors.depInk,
});
const TierScore = styled('div', {
  fontSize: '11px',
  color: dependabilityV2Colors.depInk,
});
const Bar = styled('div', {
  position: 'relative',
  display: 'flex',
  height: '6px',
  borderRadius: '100px',
  overflow: 'visible',
  marginBottom: '8px',
});
const Segment = styled('div', { height: '6px' });
const Marker = styled('div', {
  position: 'absolute',
  top: '-4px',
  width: '13px',
  height: '13px',
  borderRadius: '50%',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.2)',
  transform: 'translateX(-50%)',
});
const Labels = styled('div', {
  display: 'flex',
});
const LabelBlock = styled('div', { flex: 1, textAlign: 'right' });
const LabelName = styled('div', {
  fontSize: '$sm',
  fontWeight: 600,
  color: dependabilityV2Colors.depInk,
});
const LabelRange = styled('div', {
  fontSize: '11px',
  color: dependabilityV2Colors.depInk,
});

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  boundaries: TierBoundary[];
  score: number;
  tier: string;
  completedShifts: number;
  scoringMode: 'activation' | 'reliability';
  justify?: 'left' | 'right';
}

const rangeLabel = (b: TierBoundary, isFirst: boolean, isLast: boolean) => {
  if (isFirst) return `<${b.max + 1}`;
  if (isLast) return `${b.min}+`;
  return `${b.min}–${b.max}`;
};

// Segments render at EQUAL widths (not proportional to their score span), so
// the marker is placed piecewise: which segment the tier occupies, then the
// score's fractional position within that segment. The denominator spans to
// the NEXT tier's min (max - min + 1): a 2-decimal score like 89.5 is still
// Gold and must land inside the gold segment, not on the platinum boundary.
const markerLeft = (
  boundaries: TierBoundary[],
  score: number,
  tier: string,
): number => {
  const n = boundaries.length;
  if (n === 0) return 0;
  const clamped = clampScore(score);
  let idx = boundaries.findIndex((b) => b.tier === tier);
  if (idx === -1)
    idx = boundaries.findIndex((b) => clamped >= b.min && clamped <= b.max);
  if (idx === -1) idx = clamped < boundaries[0].min ? 0 : n - 1;
  const b = boundaries[idx];
  const span = Math.max(1, b.max - b.min + 1);
  const frac = Math.max(0, Math.min(1, (clamped - b.min) / span));
  return ((idx + frac) / n) * 100;
};

const segmentRadius = (i: number, count: number) => {
  if (i === 0) return '100px 0 0 100px';
  if (i === count - 1) return '0 100px 100px 0';
  return '0';
};

const TierPopover = ({
  isOpen,
  onRequestClose,
  boundaries,
  score,
  tier,
  completedShifts,
  scoringMode,
  justify = 'left',
}: Props) => {
  const tierLabel = boundaries.find((b) => b.tier === tier)?.label || tier;

  return (
    <Popover
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size="lg"
      justify={justify}
      // the frame shows the popover floating above the pill
      align="above"
    >
      <Content data-testid="tier-popover">
        <Title>Dependability tier</Title>
        {scoringMode === 'activation' ? (
          <Subtitle activation>
            Activation score, based on onboarding progress
          </Subtitle>
        ) : (
          <Subtitle>{`Based on ${completedShifts} shifts`}</Subtitle>
        )}
        <TierSummary>
          <PopoverMedal tier={tier} color={tierColor(tier)} />
          <div>
            <TierName>{`${tierLabel} tier`}</TierName>
            <TierScore>{`Score: ${displayScore(score)}`}</TierScore>
          </div>
        </TierSummary>
        <Bar>
          {boundaries.map((b, i) => (
            <Segment
              key={b.tier}
              css={{
                width: `${100 / boundaries.length}%`,
                backgroundColor: tierColor(b.tier),
                // the Marker is Bar's last child, so :last-child can't round
                // the final segment — set end caps explicitly by index
                borderRadius: segmentRadius(i, boundaries.length),
              }}
            />
          ))}
          <Marker
            data-testid="tier-popover-marker"
            style={{
              left: `${markerLeft(boundaries, score, tier)}%`,
              border: `1px solid ${tierColor(tier)}`,
            }}
          />
        </Bar>
        <Labels>
          {boundaries.map((b, i) => (
            <LabelBlock key={b.tier}>
              <LabelName>{b.label}</LabelName>
              <LabelRange>
                {rangeLabel(b, i === 0, i === boundaries.length - 1)}
              </LabelRange>
            </LabelBlock>
          ))}
        </Labels>
      </Content>
    </Popover>
  );
};

export default TierPopover;
